import type { LegalDocPair } from '../types'

/**
 * Subprocesadores: los proveedores que tratan datos personales por cuenta de
 * véltica. Sale de lo que el código llama de verdad (base de datos y acceso,
 * alojamiento y almacenamiento, pagos, Google, correo, dominios, imágenes de
 * archivo e integración opcional de marketing). Al añadir uno, se añade aquí
 * ANTES de usarlo: el DPA promete avisar de los cambios con 30 días.
 */
export const SUBPROCESSORS: LegalDocPair = {
  en: {
    title: 'Subprocessors',
    summary: 'The companies that process personal data on véltica’s behalf.',
    intro: [
      'véltica uses the companies below to run the Platform. They process personal data on our behalf under written agreements. This page is referred to by our [Privacy Policy](doc:privacy) and our [Data Processing Addendum](doc:dpa).',
    ],
    sections: [
      {
        title: 'Our subprocessors',
        blocks: [
          {
            table: {
              head: ['Provider', 'What it does for us', 'Personal data involved'],
              rows: [
                ['Supabase, Inc.', 'Database and authentication', 'Account data, project content, and records of forms, bookings, orders and contacts'],
                ['Cloudflare, Inc.', 'Hosting and delivery of the Platform and of published Sites, file storage, content delivery network, DNS and security certificates for custom domains, page screenshots, and bot protection at sign-in (Turnstile)', 'Published content, IP addresses of visitors in transit, uploaded files, browser and device signals at sign-in'],
                ['Stripe, Inc.', 'Payments for memberships and domains, and payments for sellers through connected accounts', 'Billing data, seller account data, and buyer payment data (Stripe is an independent controller for payment processing)'],
                ['Google LLC', 'Sign in with Google; optional Google Calendar sync; the Gemini API for AI features', 'Name, email and photo; calendar busy times and events for bookings; AI inputs and outputs'],
                ['Resend, Inc.', 'Sending transactional email: form notifications, booking confirmations, reminders and changes, order, shipping and refund notices to buyers, sale and dispute notices to sellers, account emails', 'Email addresses and the content of those messages'],
                ['Porkbun LLC', 'Domain registration', 'Registrant contact data and domain names'],
                ['Unsplash', 'Searching and delivering stock photos', 'Search terms and the images requested'],
                ['Registry lookup services', 'Checking whether a domain name is available', 'Domain names searched'],
                ['Omnisend', 'Optional email marketing integration, only when a Site owner enables it', 'Contact email addresses and the consent fields the owner chooses to sync'],
              ],
            },
          },
          'Integrations that a Site owner sets up directly with a third party, such as analytics or advertising tools added to a Site, are chosen and controlled by the owner and are not our subprocessors.',
        ],
      },
      {
        title: 'Changes to this list',
        blocks: [
          'We will update this page before we start using a new subprocessor, and we will notify customers by email or inside the Platform at least 30 days in advance, as described in the [Data Processing Addendum](doc:dpa), which also explains how to object. Questions: {email}.',
        ],
      },
    ],
  },

  es: {
    title: 'Subprocesadores',
    summary: 'Las empresas que tratan datos personales por cuenta de véltica.',
    intro: [
      'véltica usa las empresas de abajo para operar la Plataforma. Tratan datos personales por nuestra cuenta mediante acuerdos escritos. A esta página remiten nuestra [Política de privacidad](doc:privacy) y nuestro [Acuerdo de procesamiento de datos](doc:dpa).',
    ],
    sections: [
      {
        title: 'Nuestros subprocesadores',
        blocks: [
          {
            table: {
              head: ['Proveedor', 'Qué hace por nosotros', 'Datos personales implicados'],
              rows: [
                ['Supabase, Inc.', 'Base de datos y autenticación', 'Datos de cuenta, contenido de proyectos y registros de formularios, reservas, pedidos y contactos'],
                ['Cloudflare, Inc.', 'Alojamiento y entrega de la Plataforma y de los Sitios publicados, almacenamiento de archivos, red de entrega de contenido, DNS y certificados de seguridad de dominios propios, capturas de páginas, y protección contra bots al entrar (Turnstile)', 'Contenido publicado, direcciones IP de visitantes en tránsito, archivos subidos, señales del navegador y del dispositivo al entrar'],
                ['Stripe, Inc.', 'Pagos de membresías y dominios, y pagos de los vendedores mediante cuentas conectadas', 'Datos de facturación, datos de cuenta del vendedor y datos de pago de compradores (Stripe es responsable independiente del procesamiento de pagos)'],
                ['Google LLC', 'Acceso con Google; sincronización opcional de Google Calendar; API Gemini para las funciones de IA', 'Nombre, correo y foto; horas ocupadas y eventos del calendario para reservas; entradas y salidas de IA'],
                ['Resend, Inc.', 'Envío de correo transaccional: avisos de formularios, confirmaciones, recordatorios y cambios de reservas, avisos de pedido, envío y reembolso a compradores, avisos de venta y de disputa a vendedores, correos de cuenta', 'Direcciones de correo y el contenido de esos mensajes'],
                ['Porkbun LLC', 'Registro de dominios', 'Datos de contacto del titular y nombres de dominio'],
                ['Unsplash', 'Búsqueda y entrega de fotos de archivo', 'Términos de búsqueda y las imágenes solicitadas'],
                ['Servicios de consulta de registros', 'Comprobar si un nombre de dominio está disponible', 'Nombres de dominio buscados'],
                ['Omnisend', 'Integración opcional de marketing por correo, solo cuando el titular de un Sitio la activa', 'Correos de contactos y los campos de consentimiento que el titular decida sincronizar'],
              ],
            },
          },
          'Las integraciones que un titular de Sitio configura directamente con un tercero, como herramientas de analítica o publicidad añadidas a un Sitio, las elige y controla el titular y no son subprocesadores nuestros.',
        ],
      },
      {
        title: 'Cambios en esta lista',
        blocks: [
          'Actualizaremos esta página antes de empezar a usar un nuevo subprocesador y avisaremos a los clientes por correo o dentro de la Plataforma con al menos 30 días de antelación, como se describe en el [Acuerdo de procesamiento de datos](doc:dpa), que también explica cómo oponerse. Preguntas: {email}.',
        ],
      },
    ],
  },
}
